import { useState } from "react";
import { Wenn } from "@/auth/operations";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, LayoutTemplate, RefreshCw, Trash2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useWorkspaceTemplate } from "@/api/queries/workspaceTemplates";
import { useManagedRepos } from "@/api/queries/gitContent";
import { useRepoDrafts } from "@/api/queries/repoLifecycle";
import { useDeleteWorkspaceTemplate } from "@/api/mutations/gitContent";
import { useSetTemplateOwner } from "@/api/mutations/ownership";
import { SetOwnerDialog, OwnerValue } from "@/components/ownership/SetOwnerDialog";
import { PendingChangeRow } from "@/components/repo-lifecycle/PendingChangeRow";
import { RepoContentPanel } from "@/components/repo-content/RepoContentPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ApiError } from "@/api/client";

// #290 (UC-2): Vorlagen-Detail — Fach-Ebene oben („wer nutzt das"), darunter der GETEILTE
// Content-Baustein (Dateien + Entwurf/Freigabe/Version). Dieselbe Komponente wie auf
// /repositories — eine Implementierung, zwei Blickwinkel.
export function WorkspaceTemplateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useWorkspaceTemplate(id);
  const remove = useDeleteWorkspaceTemplate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // #292 (UC-2): Verantwortlichen setzen (owns-Kante, admin-only).
  const [ownerOpen, setOwnerOpen] = useState(false);
  const setOwner = useSetTemplateOwner();
  // Der Content-Baustein läuft über die ManagedRepo-Sicht (er IST die Git-Werkbank).
  // Aufgelöst wird über die SERVER-Zusage `repoId` (Review-Fund: vorher aus dem opaken
  // Handle geraten); `?? id` nur als Version-Skew-Fallback für einen älteren Master.
  const repos = useManagedRepos();
  const repo = (repos.data ?? []).find((r) => r.id === (data?.repoId ?? id)) ?? null;
  // #291 (UC-3): offene Entwürfe der Vorlage sichtbar machen — mit Sprung in den Stand.
  const drafts = useRepoDrafts(data?.repoId ?? id);
  const [, setSearchParams] = useSearchParams();
  const offene = drafts.data ?? [];

  const errorMessage =
    error instanceof ApiError && error.status === 404
      ? `Vorlage "${id}" nicht gefunden`
      : (error as Error | undefined)?.message;

  if (isLoading && !data) {
    return (
      <SectionCard testId="detail-loading">
        <div className="flex items-center gap-3 py-8 text-sm text-text3">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Vorlage…
        </div>
      </SectionCard>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-5">
        <BackLink to="/workspace-templates" label="Workspace-Vorlagen" />
        <SectionCard testId="detail-error">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-danger" />
            <div className="text-sm text-text2">{errorMessage ?? "Konnte nicht laden"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo="/workspace-templates"
        backLabel="Workspace-Vorlagen"
        icon={LayoutTemplate}
        title={data.name}
        badge={<Badge variant="outline">Vorlage</Badge>}
        sub="Start-Inhalt für Agent-Workspaces — Profile verweisen darauf, neue Agents forken daraus."
        actions={
          // #299 (Review M-2): ohne dieses Gate bot die Seite einem plugin-author einen
          // Löschen-Knopf an, der nach dem Bestätigungsdialog sicher im 403 endet
          // (braucht git-content:delete). Eine destruktive Zusage, die nicht hält.
          <Wenn op="workspace-template.delete">
          <Button
            variant="destructive"
            size="sm"
            // #290 (UC-5): Sperre VORHER sichtbar machen statt 409 — der Guard selbst
            // bleibt serverseitig (die UI ist nur die Höflichkeit).
            disabled={data.usedByProfiles.length > 0}
            title={
              data.usedByProfiles.length > 0
                ? `Wird von ${data.usedByProfiles.map((p) => p.name).join(", ")} verwendet — zuerst dort umhängen.`
                : undefined
            }
            onClick={() => setDeleteOpen(true)}
            data-testid="template-delete"
          >
            <Trash2 className="h-4 w-4" /> Löschen
          </Button>
          </Wenn>
        }
      />

      {/* #293 (UC-4): ausstehende Änderungen als BUSINESS-Zeilen — Entscheidung in der
          Zeile, Diff als Dialog. Kein ref-Umschalten, kein Branch-Vokabular. */}
      {/* Review-Fund 3: Ladefehler ≠ „keine Entwürfe". */}
      {drafts.isError && (
        <p className="text-[12.5px] text-danger">Liste der ausstehenden Änderungen konnte nicht geladen werden.</p>
      )}
      {offene.length > 0 && data.repoId && (
        <SectionCard
          title={`Ausstehende Änderungen (${offene.length})`}
          description="Änderungen an dieser Vorlage — erst nach der Freigabe wirksam (neue Agents forken dann den neuen Stand)."
          testId="template-pending-section"
        >
          <ul className="space-y-1.5">
            {offene.map((d) => (
              <PendingChangeRow
                key={d.branch}
                repoId={data.repoId!}
                branch={d.branch}
                title={d.title}
                state={d.state}
                createdBy={d.createdBy}
                canApprove={d.canApprove}
                discardDescription={`„${d.title}" wird verworfen — die Vorlage bleibt unverändert. Das lässt sich nicht rückgängig machen.`}
                onDecided={() =>
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.delete("ref");
                      next.delete("file");
                      next.delete("path");
                      return next;
                    },
                    { replace: true },
                  )
                }
                testId={`template-pending-${d.slug}`}
              />
            ))}
          </ul>
        </SectionCard>
      )}

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1fr_340px]">
        {/* Inhalt (Dateien + Entwurf/Freigabe/Version) — der geteilte Baustein */}
        <div className="flex h-[calc(100vh-260px)] min-h-[480px] flex-col gap-3">
          {repos.isLoading ? (
            <SectionCard>
              <div className="flex items-center gap-3 py-6 text-sm text-text3">
                <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Inhalt…
              </div>
            </SectionCard>
          ) : repo ? (
            <RepoContentPanel key={repo.id} repo={repo} />
          ) : (
            <SectionCard>
              <p className="text-[13px] text-text3">Inhalt momentan nicht verfügbar (Repo-Sicht nicht ladbar).</p>
            </SectionCard>
          )}
        </div>

        <PropGrid
          rows={[
            ["Quelle", <span className="mono text-xs">{data.repoFullName}</span>],
            [
              "Verwendet von",
              data.usedByProfiles.length === 0 ? (
                <span className="text-text3">kein Profil</span>
              ) : (
                <div className="flex flex-col gap-0.5">
                  {data.usedByProfiles.map((p) => (
                    <Link key={p.id} to={`/settings-profiles/${encodeURIComponent(p.id)}`} className="text-accent-text hover:underline">
                      {p.name}
                    </Link>
                  ))}
                </div>
              ),
            ],
            ["Agents daraus", String(data.agentCount)],
            [
              "Verantwortlich",
              // #292 (UC-2): owns-Kante auf dem Vorlagen-Repo (wie beim Seed).
              <span className="flex items-center gap-2" data-testid="template-owner">
                <OwnerValue ownerId={data.ownerId} />
                <Wenn op="workspace-template.set-owner">
                  <button
                    className="text-[11.5px] text-accent-text hover:underline"
                    onClick={() => setOwnerOpen(true)}
                    data-testid="template-owner-edit"
                  >
                    ändern
                  </button>
                </Wenn>
              </span>,
            ],
          ]}
        />
      </div>

      {ownerOpen && (
        <SetOwnerDialog
          open
          onOpenChange={setOwnerOpen}
          artifactLabel={`die Vorlage "${data.name}"`}
          currentOwnerId={data.ownerId ?? null}
          isPending={setOwner.isPending}
          onSave={(ownerId) => {
            if (!id) return;
            setOwner.mutate(
              { id, ownerId },
              {
                onSuccess: () => {
                  toast.success(ownerId ? "Verantwortlicher gesetzt" : "Verantwortung ans System zurückgegeben");
                  setOwnerOpen(false);
                },
                onError: (e) => toast.error(`Setzen fehlgeschlagen: ${(e as Error).message}`),
              },
            );
          }}
        />
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(o) => {
          setDeleteOpen(o);
          // Review-Fund: die alte 409-Meldung darf einen NEUEN Dialog-Durchgang nicht
          // vorbelasten (Profil inzwischen umgehängt → Meldung wäre falsch).
          if (!o) setDeleteError(null);
        }}
        title="Vorlage löschen"
        description="Die Vorlage wird archiviert (Inhalt und Historie bleiben unter einem datierten Namen erhalten), der Name ist danach sofort wieder frei. Bestehende Agent-Workspaces sind nicht betroffen."
        confirmText="Löschen (archivieren)"
        confirmVariant="destructive"
        requiredTypeText={data.name}
        isPending={remove.isPending}
        errorMessage={deleteError ?? undefined}
        onConfirm={() => {
          if (!id) return;
          setDeleteError(null);
          remove.mutate(id, {
            onSuccess: ({ archivedAs }) => {
              toast.success(`Archiviert als ${archivedAs}`);
              navigate("/workspace-templates", { replace: true });
            },
            onError: (e) => {
              const status = e instanceof ApiError ? e.status : 0;
              setDeleteError(
                status === 409
                  ? "Die Vorlage wird von einem Settings-Profil verwendet — bitte zuerst dort umhängen."
                  : (e as Error).message,
              );
            },
          });
        }}
      />
    </div>
  );
}
